import { useEffect, useRef, useState } from "react";
import { formatFaps } from "@gooner/shared";
import { CROWN_GOLD, CrownMark } from "../control/ui/Crown";
import { AuthModal } from "../control/components/AuthModal";
import { cx } from "../control/ui/primitives";
import { FAPS_AUTH_REASON, trackFapsPillOpened, useFaps, useFapsWalletSync } from "../lib/faps";
import { useCountUp } from "../lib/useCountUp";
import { FapsWelcomeCard } from "./FapsWelcomeCard";

/**
 * The Faps pill — the balance, worn in the bar on every surface.
 *
 * Sits beside the screen-code chip and speaks its glass vocabulary: a rounded pill,
 * THE CROWN AND A NUMBER, and no word — the crown beside a number is what a Faps
 * balance looks like, site-wide, and spelling it out here would make the one place
 * people see it most the one place it is said twice. Before the wallet answers it
 * shows a placeholder of the same width, so the bar does not reflow when the number
 * lands.
 *
 * Signed out it is the standing nudge — the crown and "Earn" — and owns its own AuthModal: the
 * pill is NOT inside the ☰, so the menu-unmount rule that keeps dialogs out of
 * MenuAccountItems does not bind here (the FapBar precedent).
 *
 * THE CHEST INDICATOR NEVER LOOPS. A wall plays for hours and this bar never idles; a
 * perpetual pulse in the corner of a television would be the most-noticed thing on the
 * site. So: a static amber dot and a gold ring while the chest is ready, and ONE pulse
 * on the edge where it becomes ready — the moment worth a glance.
 *
 * THE NUMBER ROLLS. A tap, a chest, a purchase moves the balance and the pill counts to it
 * (useCountUp) rather than jumping — the one animation on the number, and it snaps under
 * reduced motion. The label is the rolling value; the accessible name is the real one.
 *
 * Renders nothing until the settings say FAPS is on (lib/faps.ts, rule 2). It also owns
 * the wallet's fetch driver, being the one element every surface mounts — which is why
 * the welcome card is its too (the AuthModal precedent): the grant lands on the wallet's
 * first read, and the pill is where that read happens.
 */
export function FapsPill({ onOpen }: { onOpen: () => void }) {
  useFapsWalletSync();
  const { enabled, user, balance, chestReady } = useFaps();
  const shown = useCountUp(balance);
  const [authOpen, setAuthOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  // What the previous render knew: whether a wallet had answered, and whether the chest
  // was ready then. A pulse needs a wallet that was ALREADY known to flip from not-ready
  // to ready — midnight passing under an open tab — not the first answer of a reload,
  // which is null→ready every time and would pulse on every page.
  const prev = useRef<{ known: boolean; ready: boolean }>({ known: false, ready: false });
  useEffect(() => {
    const known = balance !== null;
    if (known && prev.current.known && chestReady && !prev.current.ready) setPulse(true);
    prev.current = { known, ready: chestReady };
  }, [chestReady, balance]);

  if (!enabled) return null;

  if (!user) {
    return (
      <>
        <button
          type="button"
          onClick={() => setAuthOpen(true)}
          /* Hidden below `sm` with the counter — see the signed-in fork. The modal below
             stays mounted at every width: it is a portal, and closing over it on a phone
             would strand an open sign-up the moment the viewport crossed the breakpoint. */
          className="hidden items-center gap-1.5 rounded-full border border-white/15 bg-white/5 py-1.5 pl-2.5 pr-3 text-sm text-zinc-400 transition-colors hover:bg-white/15 hover:text-white sm:flex"
          aria-label="Earn Faps — create a free account"
          title="Earn Faps"
        >
          <CrownMark size={14} tone="muted" />
          <span className="whitespace-nowrap">Earn</span>
        </button>
        {authOpen && (
          <AuthModal initialMode="register" reason={FAPS_AUTH_REASON} onClose={() => setAuthOpen(false)} />
        )}
      </>
    );
  }

  const label = balance === null ? "····" : formatFaps(shown ?? balance);
  return (
    <>
    <button
      type="button"
      onClick={() => {
        trackFapsPillOpened("pill");
        onOpen();
      }}
      onAnimationEnd={() => setPulse(false)}
      className={cx(
        /* PHONES DO NOT GET A FAPS SLOT IN THE BAR — this fork or the nudge above.
           Below `sm` the right edge is [code][☰] in about 390px, and Faps is the thing
           that gives: signed in, FapsMenuRow already renders this number inside the ☰
           with the same crown and the same chest dot, so the bar was the second copy.
           Signed out there is no menu row to inherit the nudge, and a phone simply does
           not advertise Faps until the ☰ is open — the bar's width is worth more than
           the impression. Both forks keep their portals mounted at every width. */
        "relative hidden items-center gap-1.5 rounded-full border py-1.5 pl-2.5 pr-3 text-sm transition-colors hover:bg-white/15 sm:flex",
        chestReady ? "border-[#f5c518]/40 bg-white/5 ring-1 ring-[#f5c518]/30" : "border-white/15 bg-white/5",
        pulse ? "faps-pulse" : "",
      )}
      aria-label={`Faps: ${balance === null ? "loading" : formatFaps(balance)}${chestReady ? ". Daily chest ready." : ""}`}
      title={chestReady ? "Your daily chest is ready" : "Your Faps"}
    >
      <CrownMark size={14} />
      <span className="tabular-nums font-semibold" style={{ color: CROWN_GOLD }}>
        {label}
      </span>
      {chestReady ? (
        <span
          aria-hidden
          className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.9)]"
        />
      ) : null}
    </button>
    <FapsWelcomeCard />
    </>
  );
}
